import { ArrowLeft, ArrowRightLeft, Pencil } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useCatalogoFarmacosControllerObter } from '@/api/generated/admin-catalogo-farmacos/admin-catalogo-farmacos';
import { ApresentacoesDoFarmaco } from '@/features/admin/catalogo-farmacos/components/ApresentacoesDoFarmaco';
import { FarmacoFormDialogo } from '@/features/admin/catalogo-farmacos/components/FarmacoFormDialogo';
import { FundirFarmacoDialogo } from '@/features/admin/catalogo-farmacos/components/FundirFarmacoDialogo';
import { EstadoCarregando, EstadoErro } from '@/shared/components/estados';
import { ROTAS } from '@/shared/lib/rotas';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Separator } from '@/shared/ui/separator';

/**
 * Fármaco do catálogo: princípio ativo e apresentações comerciais (B14-1).
 *
 * Os dois níveis dividem a tela pelo mesmo motivo do catálogo de exames — uma
 * apresentação só faz sentido junto do princípio ativo dela, e navegar entre
 * duas telas para cadastrar "Vetoryl 30 mg" seria trabalho sem informação nova.
 */
export function FarmacoDetalhePage() {
  const { id = '' } = useParams<{ id: string }>();
  const { pode } = usePermissoes();

  const [emEdicao, setEmEdicao] = useState(false);
  const [fundindo, setFundindo] = useState(false);

  const { data: farmaco, isPending, isError, refetch } = useCatalogoFarmacosControllerObter(id);

  const podeEditar = pode('admin_catalogo_farmacos.editar');

  if (isPending) return <EstadoCarregando rotulo="Carregando fármaco..." />;
  if (isError || !farmaco) return <EstadoErro aoTentarDeNovo={() => void refetch()} />;

  return (
    <div className="space-y-6">
      <Link
        to={ROTAS.adminCatalogoFarmacos}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-brand transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Voltar ao catálogo
      </Link>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 p-6 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-semibold text-foreground">{farmaco.nome}</h2>
              {!farmaco.ativo && (
                <Badge variant="outline" className="text-destructive border-destructive/40">
                  Desativado
                </Badge>
              )}
              {farmaco.classeTerapeutica && (
                <Badge variant="outline">{farmaco.classeTerapeutica}</Badge>
              )}
            </div>

            {farmaco.substituidoPorNome && (
              <p className="text-sm text-amber-600 mt-2">
                → fundido em {farmaco.substituidoPorNome}. A leitura dos tratamentos antigos segue
                a seta; os registros em si não foram reescritos.
              </p>
            )}

            {farmaco.sinonimos.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                <span className="text-xs text-muted-foreground">Sinônimos:</span>
                {farmaco.sinonimos.map((sinonimo) => (
                  <Badge key={sinonimo} variant="outline" className="font-normal">
                    {sinonimo}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {podeEditar && (
            <div className="flex items-center gap-2">
              {farmaco.ativo && !farmaco.substituidoPorId && (
                <Button variant="outline" onClick={() => setFundindo(true)}>
                  <ArrowRightLeft className="w-4 h-4" />
                  Fundir em outro
                </Button>
              )}
              <Button variant="outline" onClick={() => setEmEdicao(true)}>
                <Pencil className="w-4 h-4" />
                Editar fármaco
              </Button>
            </div>
          )}
        </div>

        <Separator />

        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
          <div>
            <dt className="text-muted-foreground">Unidade de dose padrão</dt>
            <dd className="text-foreground">{farmaco.unidadeDosePadrao ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Apresentações ativas</dt>
            <dd className="text-foreground tabular-nums">{farmaco.totalApresentacoes}</dd>
          </div>
          {/* O número que justifica desativar em vez de apagar. */}
          <div>
            <dt className="text-muted-foreground">Tratamentos que usam</dt>
            <dd className="text-foreground tabular-nums">{farmaco.totalEmUso}</dd>
          </div>
        </dl>
      </div>

      <ApresentacoesDoFarmaco farmaco={farmaco} podeEditar={podeEditar} />

      <FarmacoFormDialogo
        aberto={emEdicao}
        aoFechar={() => setEmEdicao(false)}
        farmaco={farmaco}
      />

      {fundindo && (
        <FundirFarmacoDialogo aberto aoFechar={() => setFundindo(false)} farmaco={farmaco} />
      )}
    </div>
  );
}
